import produce from 'immer';
import { handleActions } from 'redux-actions';
import { v4 as uuidv4 } from 'uuid';

import { SURVEY_DEFAULT_OPTIONS } from '@/config';
import { deepCopy, nestedAssign } from '@/utils/ObjectUtils';

const initialState = {
  id: null,
  name: '',
  json: {
    specs: {
      protocol: null,
      firmware: null,
      frame: null,
    },
    commands: [],
    fences: [],
  },
  collisions: [],
  elevations: [],
};

export default handleActions(
  {
    'MISSION/RESET': (state, action) => initialState,
    'MISSION/SET_SPECS': (state, action) =>
      produce(state, (draft) => {
        draft.json.specs = action.payload.specs;

        //! 임시
        if (action.payload.specs.protocol === 'dji') {
          draft.json.specs.model = 'M4TD';
          draft.json.specs.config = {
            securityHeight: 20,
            autoFlightSpeed: 10,
            transitionalSpeed: 10,
            height: 50,
            rthHeight: 50,
          };
        }
      }),
    'MISSION/CHANGE_NAME': (state, action) =>
      produce(state, (draft) => {
        draft.name = action.payload.name;
      }),
    'MISSION/LOAD': (state, action) =>
      produce(state, (draft) => {
        const { id, name, json } = action.payload;
        draft.id = id || uuidv4();
        draft.name = name;
        draft.json.specs = json.specs;
        draft.json.commands = json.commands;
        draft.json.fences = json.fences || [];
      }),
    'MISSION/SET_COLLISIONS': (state, action) =>
      produce(state, (draft) => {
        draft.collisions = action.payload.collisions;
      }),
    'MISSION/SET_ELEVATIONS': (state, action) =>
      produce(state, (draft) => {
        draft.elevations = action.payload.elevations;
      }),
    'MISSION/RESET_COMMANDS': (state, action) =>
      produce(state, (draft) => {
        draft.json.commands = [];
      }),
    'MISSION/REMOVE_COMMAND': (state, action) =>
      produce(state, (draft) => {
        const { index } = action.payload;

        const commands = [...state.json.commands];
        commands.splice(index, 1);
        draft.json.commands = commands;
      }),
    'MISSION/SHIFT_COMMAND': (state, action) =>
      produce(state, (draft) => {
        const { index, offset } = action.payload;

        const commands = [...state.json.commands];
        [commands[index], commands[index + offset]] = [commands[index + offset], commands[index]];
        draft.json.commands = commands;
      }),
    'MISSION/UPDATE_COMMAND': (state, action) =>
      produce(state, (draft) => {
        const { index, name, value } = action.payload;

        const commands = deepCopy(state.json.commands);
        commands[index].data[name] = value;
        draft.json.commands = commands;
      }),
    'MISSION/APPEND_WAYPOINT': (state, action) =>
      produce(state, (draft) => {
        const { waypoint } = action.payload;

        const newCommand = {
          id: uuidv4(),
          type: state.json.commands.length === 0 ? 'navTakeoff' : 'navWaypoint',
          data: {
            name: '',
            ...waypoint,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/INSERT_WAYPOINT': (state, action) =>
      produce(state, (draft) => {
        const { index, waypoint } = action.payload;

        const newCommand = {
          id: uuidv4(),
          type: 'navWaypoint',
          data: {
            name: '',
            ...waypoint,
          },
        };
        const commands = deepCopy(state.json.commands);
        commands.splice(index + 1, 0, newCommand);
        draft.json.commands = commands;
      }),
    'MISSION/MOVE_WAYPOINT': (state, action) =>
      produce(state, (draft) => {
        const { id, waypoint } = action.payload;

        const index = state.json.commands.findIndex((command) => command.id === id);
        if (index === -1) return;

        const commands = deepCopy(state.json.commands); // to avoid read-only property
        commands[index].data.position = waypoint.position;
        commands[index].data.elevation = waypoint.elevation;
        draft.json.commands = commands;
      }),
    'MISSION/EDIT_WAYPOINT': (state, action) =>
      produce(state, (draft) => {
        const { index, name, value } = action.payload;

        const commands = deepCopy(state.json.commands);
        nestedAssign(commands[index].data, name.split('.'), value);
        draft.json.commands = commands;
      }),
    'MISSION/EDIT_ALL_WAYPOINTS': (state, action) =>
      produce(state, (draft) => {
        if (state.json.commands.length === 0) return;

        const { name, value } = action.payload;

        // Relative Altitude
        if (name === 'relAltitude') {
          draft.json.commands = state.json.commands.map((command) => {
            if (
              ['navLoiterTime', 'navLoiterToAlt', 'navLoiterTurns', 'navTakeoff', 'navWaypoint', 'cusSurvey'].includes(
                command.type
              )
            ) {
              const newCommand = deepCopy(command);
              newCommand.data.altitude = value;
              return newCommand;
            }
            return command;
          });
        }
        // AGL Altitude
        else if (name === 'aglAltitude') {
          const homeElevation = state.json.commands[0].data.elevation;
          draft.json.commands = state.json.commands.map((command) => {
            if (['navLoiterTime', 'navLoiterToAlt', 'navLoiterTurns', 'navWaypoint'].includes(command.type)) {
              const newCommand = deepCopy(command);
              newCommand.data.altitude = value - homeElevation + command.data.elevation;
              return newCommand;
            }
            return command;
          });
        }
        // MSL Altitude
        else if (name === 'mslAltitude') {
          const homeElevation = state.json.commands[0].data.elevation;
          draft.json.commands = state.json.commands.map((command) => {
            if (['navLoiterTime', 'navLoiterToAlt', 'navLoiterTurns', 'navWaypoint'].includes(command.type)) {
              const newCommand = deepCopy(command);
              newCommand.data.altitude = value - homeElevation;
              return newCommand;
            }
            return command;
          });
        }
      }),
    'MISSION/APPEND_SURVEY': (state, action) =>
      produce(state, (draft) => {
        const { boundary, positions, altitude } = action.payload;

        const newCommand = {
          id: uuidv4(),
          type: 'cusSurvey',
          data: {
            ...SURVEY_DEFAULT_OPTIONS,
            boundary,
            positions,
            altitude,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/EDIT_SURVEY': (state, action) =>
      produce(state, (draft) => {
        const { index, values } = action.payload;

        const commands = deepCopy(state.json.commands);
        Object.entries(values).forEach(([name, value]) => {
          nestedAssign(commands[index].data, name.split('.'), value);
        });
        draft.json.commands = commands;
      }),
    'MISSION/APPEND_RETURN_TO_LAUNCH': (state, action) =>
      produce(state, (draft) => {
        const newCommand = { id: uuidv4(), type: 'navReturnToLaunch' };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_GRIPPER': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'doGripper',
          data: {
            gripper: 1,
            action: 0, // 0: Release, 1: Grab
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_SET_RELAY': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'doSetRelay',
          data: {
            relay: 0,
            setting: 1,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_CYCLE_RELAY': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'doRepeatRelay',
          data: {
            relay: 0,
            count: 1,
            time: 10,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_SET_SERVO': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'doSetServo',
          data: {
            servo: 1,
            pwm: 1500,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_CYCLE_SERVO': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'doRepeatServo',
          data: {
            servo: 1,
            pwm: 1000,
            count: 1,
            time: 10,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_DELAY_UNTIL': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'navDelay',
          data: {
            hold: 30,
            hour: 0,
            minute: 0,
            second: 0,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_JUMP_TO_ITEM': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'doJump',
          data: {
            item: 1,
            repeat: 10,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_CHANGE_ALTITUDE': (state, action) =>
      produce(state, (draft) => {
        const { altitude } = action.payload;

        const newCommand = {
          id: uuidv4(),
          type: 'navContinueAndChangeAlt',
          data: { mode: 1, altitude },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_CHANGE_SPEED': (state, action) =>
      produce(state, (draft) => {
        const { speed } = action.payload;

        const newCommand = {
          id: uuidv4(),
          type: 'doChangeSpeed',
          data: { type: 0, speed },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/EDIT_CHANGE_SPEED': (state, action) =>
      produce(state, (draft) => {
        const { index, name, value } = action.payload;

        const commands = deepCopy(state.json.commands);
        // 값이 undefined 인 경우 필드 제거
        if (value === undefined) {
          delete commands[index].data[name];
        } else {
          commands[index].data[name] = value;
        }
        draft.json.commands = commands;
      }),
    'MISSION/MAVLINK/APPEND_WAIT_FOR_DISTANCE': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'condDistance',
          data: {
            distance: 10,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_WAIT_FOR_YAW': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'condYaw',
          data: {
            direction: 0,
            offset: 0,
            heading: 0,
            rate: 5,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_TRIGGER_PARACHUTE': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'doParachute',
          data: {
            action: 1,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_GUIDED_ENABLE': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'navGuidedEnable',
          data: {
            enable: 1,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_GUIDED_LIMITS': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'doGuidedLimits',
          data: {
            timeout: 0,
            minAlt: 25,
            maxAlt: 100,
            horizMoveLimit: 25,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_AUTOTUNE_ENABLE': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'doAutotuneEnable',
          data: {
            enable: 1,
          },
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/APPEND_SET_ROI_NONE': (state, action) =>
      produce(state, (draft) => {
        const newCommand = {
          id: uuidv4(),
          type: 'doSetRoiNone',
        };
        draft.json.commands = [...state.json.commands, newCommand];
      }),
    'MISSION/MAVLINK/CHANGE_COMMAND_TYPE': (state, action) =>
      produce(state, (draft) => {
        const { index, type } = action.payload;

        const commands = deepCopy(state.json.commands);
        commands[index].type = type;

        switch (type) {
          case 'navLoiterTime':
            commands[index].data.loiterTime = 30;
            break;

          case 'navLoiterTurns':
            commands[index].data.turns = 1;
            break;

          default:
            break;
        }
        draft.json.commands = commands;
      }),
    'MISSION/MAVLINK/ADD_FENCE': (state, action) =>
      produce(state, (draft) => {
        const { type, data } = action.payload;

        const newFence = {
          id: uuidv4(),
          type,
          data: {
            name: '',
            ...data,
          },
        };
        draft.json.fences = [...state.json.fences, newFence];
      }),
    'MISSION/MAVLINK/EDIT_FENCE': (state, action) =>
      produce(state, (draft) => {
        const { index, values } = action.payload;

        const fences = deepCopy(state.json.fences);
        Object.entries(values).forEach(([name, value]) => {
          fences[index].data[name] = value;
        });
        draft.json.fences = fences;
      }),
    'MISSION/MAVLINK/REMOVE_FENCE': (state, action) =>
      produce(state, (draft) => {
        const { index } = action.payload;

        const fences = [...state.json.fences];
        fences.splice(index, 1);
        draft.json.fences = fences;
      }),
  },
  initialState
);
